import { useEffect, useMemo, useState } from 'react';
import { FilePlus2, Eye, EyeOff, Star, Trash2, RefreshCw, CloudOff, Handshake } from 'lucide-react';
import { Badge } from '@/components/ui/badge.jsx';
import { Button } from '@/components/ui/button.jsx';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { usePeekStore } from '@/stores/peek-store.js';
import { openCarrierPeek } from '@/lib/links.js';
import { getQuotesForAccount, deleteQuote, resyncQuote, withdrawQuote, recordOfflineBind } from '@/services/quotes.service.js';
import QuoteEntrySheet from '@/components/sheets/QuoteEntrySheet.jsx';
import Panel from './Panel.jsx';
import { formatDateOrEmpty, toDate } from '@/lib/date-display.js';

// Vision §V2 fix #9 (2026-05-20). Account Workspace surface for every
// quote on every deal under this account. Closes the gap where the
// broker could save quotes via QuoteEntrySheet with no V2 surface to
// see them after.
//
// Data source: GET /api/tracon/quotes/all?account_id=... — backend
// resolves account → deal_ids via cached pipeline and filters server-
// side so the wire payload is small.
//
// Behavior:
//   • Grouped by deal (sorted: active deals before terminal/expired).
//   • Each row clickable → opens QuoteEntrySheet in EDIT mode
//     (quotationId pre-populates the sheet via GET /quotes/:id; row.id
//     preservation on subforms per CLAUDE.md).
//   • "+ Log quote" button per group opens the sheet in NEW mode for
//     that deal.
//   • Top-level "+ Log quote" opens NEW mode pre-selected to the
//     account's first active deal (broker can change).
//   • Status badge color reflects quotes.status lifecycle
//     (received / presented / selected / declined / expired).

const STATUS_VARIANT = {
  draft: 'warning',
  received: 'info',
  presented: 'success',
  selected: 'success',
  bound: 'success',
  declined: 'muted',
  expired: 'muted',
  superseded: 'muted',
  Quoted: 'info',
  Presented: 'success',
  Bound: 'success',
};

const STATUS_LABEL = {
  draft: 'Parse draft',
  received: 'Received',
  presented: 'Presented',
  selected: 'Selected',
  bound: 'Bound',
  declined: 'Declined',
  expired: 'Expired',
  superseded: 'Superseded',
  Quoted: 'Quoted',
  Presented: 'Presented',
  Bound: 'Bound',
};

function fmtMoney(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return '$' + Number(n).toLocaleString('en-US');
}

// Canon (prompt 185). q.proposed_effective comes from quotes.effective_date,
// a Postgres `date`, so this used to render the day before.
const fmtDate = (iso) => formatDateOrEmpty(iso, { month: 'short', day: 'numeric', year: 'numeric' });

// Bind is gated on crm_quotation_id (SubmissionBuilderPanel, enforced server
// side). A quote without one showed a missing button and no reason: the badge
// only appeared on mirror_status='error', which is 2 rows, while 117 sit at
// 'skipped' or NULL with no Quotation and nothing on screen.
//
// Two different facts, so two different chips. A failed sync is a fault and
// stays red. A raw capture that was never promoted to CRM is the DESIGNED state
// ('skipped' is the intentional marker for IAT/email captures), so it explains
// itself quietly. Both open the same door: click to mirror it now.
export function mirrorChip(q) {
  if (q.mirror_status === 'error') {
    return {
      label: 'CRM',
      title: q.mirror_error ? `CRM sync failed: ${q.mirror_error}\nClick to re-sync.` : 'CRM sync failed. Click to re-sync.',
      className: 'border border-danger/40 bg-danger/10 text-danger hover:bg-danger/20',
    };
  }
  if (!q.crm_quotation_id) {
    return {
      label: 'Not in CRM',
      title: 'No CRM Quotation yet, so this quote cannot be bound. Click to create one.',
      className: 'border border-border bg-surface-strong text-text-muted hover:bg-surface',
    };
  }
  return null;
}

export default function QuotesPanel({ accountId, dealId, insuranceLine, onChange }) {
  const [quotes, setQuotes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Sheet target: { mode: 'edit'|'new', dealId, quotationId? }.
  const openPeek = usePeekStore((s) => s.openPeek);
  const [sheetTarget, setSheetTarget] = useState(null);
  // Delete flow: row trash icon → AlertDialog confirm → DELETE /quotes/:id.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Per-row CRM re-sync (mirror_status='error' badge action).
  const [resyncingId, setResyncingId] = useState(null);
  // Withdraw flow: EyeOff icon on client-visible quotes → confirm → POST /withdraw.
  const [withdrawTarget, setWithdrawTarget] = useState(null);
  const [withdrawing, setWithdrawing] = useState(false);
  // Offline-bind flow: Handshake icon → confirm → POST /record-bind. Records a
  // bind agreed offline (closing table, phone) with the quoted effective date
  // held, and routes the client's Tower link straight into signing.
  const [bindTarget, setBindTarget] = useState(null);
  const [bindingOffline, setBindingOffline] = useState(false);
  // Bump to force a refetch after save (parent onChange invalidates
  // the workspace fetch; we add a local bump as belt-and-suspenders
  // for the case where parent doesn't pass onChange).
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!accountId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getQuotesForAccount(accountId)
      .then((r) => { if (!cancelled) setQuotes(r?.quotations || r?.quotes || []); })
      .catch((err) => { if (!cancelled) setError(err); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [accountId, refreshKey]);

  // Group by deal. Sort: most recent quote first within each deal;
  // groups ordered by deal name alpha. Deal name comes off the quote
  // itself (q.deal_name) — backend resolves Policy.name.
  const grouped = useMemo(() => {
    const byDeal = new Map();
    for (const q of quotes || []) {
      const dealId = q.deal_id || '(no-deal)';
      if (!byDeal.has(dealId)) {
        byDeal.set(dealId, {
          deal_id: dealId,
          deal_name: q.deal_name || '(no deal name)',
          quotes: [],
        });
      }
      byDeal.get(dealId).quotes.push(q);
    }
    // The focused deal must ALWAYS have a group — even with zero quotes —
    // so its "Log quote" affordance exists and the broker isn't funneled
    // to a prior-term group's button. (Fresh renewals have no quotes yet;
    // without this the panel showed only prior-term deals.)
    if (quotes && dealId && !byDeal.has(dealId)) {
      byDeal.set(dealId, { deal_id: dealId, deal_name: '', quotes: [] });
    }
    // Sort each group's quotes: client-visible first, then by created_at desc.
    for (const g of byDeal.values()) {
      g.quotes.sort((a, b) => {
        if (a.client_visible !== b.client_visible) return b.client_visible ? 1 : -1;
        const at = toDate(a.created_at)?.getTime() ?? 0;
        const bt = toDate(b.created_at)?.getTime() ?? 0;
        return bt - at;
      });
    }
    return Array.from(byDeal.values()).sort((a, b) => {
      // Focused deal first (we're literally on its record — its quotes are the
      // primary thing); the rest ("prior terms") alpha beneath.
      if (dealId) {
        if (a.deal_id === dealId) return -1;
        if (b.deal_id === dealId) return 1;
      }
      return a.deal_name.localeCompare(b.deal_name);
    });
  }, [quotes, dealId]);

  // Default deal for the top-level "+ Log quote": ALWAYS the focused deal
  // when we're on a deal record. The old grouped[0] default picked the
  // alpha-first deal WITH EXISTING QUOTES — on a fresh renewal (zero quotes
  // yet) that was the expiring prior-term deal, so every new quote landed
  // on the wrong term (N8193P 2026-06-12).
  const firstDealId = dealId || grouped[0]?.deal_id || null;

  function openEdit(q) {
    // Pass the full list row as a seed so the sheet renders the headline
    // instantly instead of waiting on getQuote (the ~1s "eyeball" delay).
    setSheetTarget({ mode: 'edit', dealId: q.deal_id, quotationId: q.id, seed: q });
  }
  function openNew(dealId) {
    if (!dealId) return;
    setSheetTarget({ mode: 'new', dealId, quotationId: null });
  }
  function handleSaved() {
    setSheetTarget(null);
    setRefreshKey((k) => k + 1);
    if (typeof onChange === 'function') onChange();
  }
  async function doDelete() {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      await deleteQuote(deleteTarget.id);
      toast.success('Quote deleted');
      setDeleteTarget(null);
      setRefreshKey((k) => k + 1);
      if (typeof onChange === 'function') onChange();
    } catch (err) {
      toast.error(`Delete failed: ${err.message || 'unknown error'}`);
    } finally {
      setDeleting(false);
    }
  }

  async function doResync(q, e) {
    if (e) e.stopPropagation();
    if (resyncingId) return;
    setResyncingId(q.id);
    try {
      await resyncQuote(q.id);
      toast.success('Re-synced to CRM');
      setRefreshKey((k) => k + 1);
    } catch (err) {
      toast.error(`CRM sync failed: ${err.message || 'unknown error'}`);
    } finally {
      setResyncingId(null);
    }
  }

  async function doRecordBind() {
    if (!bindTarget || bindingOffline) return;
    setBindingOffline(true);
    try {
      await recordOfflineBind(bindTarget.deal_id, bindTarget.id);
      toast.success('Bind recorded. The Tower link now goes straight to signing.');
      setBindTarget(null);
      setRefreshKey((k) => k + 1);
      if (typeof onChange === 'function') onChange();
    } catch (err) {
      toast.error(`Record bind failed: ${err.message || 'unknown error'}`);
    } finally {
      setBindingOffline(false);
    }
  }

  async function doWithdraw() {
    if (!withdrawTarget || withdrawing) return;
    setWithdrawing(true);
    try {
      await withdrawQuote(withdrawTarget.id);
      toast.success('Quote withdrawn from Tower');
      setWithdrawTarget(null);
      setRefreshKey((k) => k + 1);
      if (typeof onChange === 'function') onChange();
    } catch (err) {
      toast.error(`Withdraw failed: ${err.message || 'unknown error'}`);
    } finally {
      setWithdrawing(false);
    }
  }

  const totalCount = quotes ? quotes.length : null;

  return (
    <>
      <Panel
        title="Quotes"
        count={totalCount}
        empty={null}
        footer={loading ? <span className="text-text-dim">Loading…</span> : null}
      >
        <div className="mb-2 flex items-center justify-between">
          <p className="text-[11px] text-text-dim">
            {error ? `Couldn't load quotes: ${error.message || 'unknown'}`
              : !quotes ? null
              : quotes.length === 0 ? 'No quotes on any deal under this account yet.'
              : `${quotes.length} quote${quotes.length === 1 ? '' : 's'} across ${grouped.length} deal${grouped.length === 1 ? '' : 's'}`}
          </p>
          {firstDealId ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => openNew(firstDealId)}
              className="h-7 gap-1.5 px-2 text-xs"
            >
              <FilePlus2 size={12} strokeWidth={1.5} />
              Log quote
            </Button>
          ) : null}
        </div>

        {grouped.length === 0 ? null : (
          <ul className="space-y-3">
            {grouped.map((g, gi) => {
              // When focused on a deal, that deal's own header is redundant
              // (you're on its record) — render its quotes bare. Other deals
              // are "prior terms": keep their headers, and drop a divider
              // before the first one so they read as secondary access.
              const isFocused = !!dealId && g.deal_id === dealId;
              const firstPrior = !!dealId && !isFocused &&
                grouped.findIndex((x) => x.deal_id !== dealId) === gi;
              return (
              <li key={g.deal_id} className={isFocused ? '' : 'rounded-card border border-border bg-bg/30 p-2'}>
                {firstPrior ? (
                  <div className="mb-2 mt-1 border-t border-border pt-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-text-dim">
                    Prior terms
                  </div>
                ) : null}
                {isFocused ? (
                  <div className="mb-1.5 flex items-center justify-end">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openNew(g.deal_id)}
                      className="h-6 gap-1 px-1.5 text-[10px] text-text-dim hover:text-accent"
                      aria-label={`Log quote on ${g.deal_name}`}
                    >
                      <FilePlus2 size={11} strokeWidth={1.5} />
                      Log quote
                    </Button>
                  </div>
                ) : (
                <header className="mb-1.5 flex items-baseline justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate text-xs font-semibold uppercase tracking-wide text-text">
                      {g.deal_name}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => openNew(g.deal_id)}
                    className="h-6 gap-1 px-1.5 text-[10px] text-text-dim hover:text-accent"
                    aria-label={`Log quote on ${g.deal_name}`}
                  >
                    <FilePlus2 size={11} strokeWidth={1.5} />
                    Log quote
                  </Button>
                </header>
                )}
                {g.quotes.length === 0 ? (
                  <p className="px-2 py-1 text-[11px] italic text-text-dim">
                    No quotes logged yet.
                  </p>
                ) : (
                  <ul className="divide-y divide-border">
                    {g.quotes.map((q) => (
                      <li key={q.id}>
                        <button
                          type="button"
                          onClick={() => openEdit(q)}
                          className="block w-full rounded-card px-2 py-2 text-left transition hover:bg-border/30"
                        >
                          <div className="flex items-baseline justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-baseline gap-2">
                                {q.carrier_id ? (
                                  <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={(e) => { e.stopPropagation(); openPeek(openCarrierPeek(q.carrier_id)); }}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); openPeek(openCarrierPeek(q.carrier_id)); } }}
                                    className="truncate text-sm font-medium text-text hover:underline cursor-pointer"
                                  >
                                    {q.carrier || '(no carrier)'}
                                  </span>
                                ) : (
                                  <span className="truncate text-sm font-medium text-text">
                                    {q.carrier || '(no carrier)'}
                                  </span>
                                )}
                                <span className="font-mono text-xs text-text-muted">
                                  {fmtMoney(q.premium)}
                                </span>
                                {q.client_visible ? (
                                  <Eye size={11} strokeWidth={1.5} className="text-info" aria-label="Client visible" />
                                ) : null}
                                {q.broker_recommended ? (
                                  <Star size={11} strokeWidth={1.5} className="text-warning" aria-label="Broker recommended" />
                                ) : null}
                              </div>
                              <div className="mt-0.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[10px] text-text-dim">
                                {q.proposed_effective ? (
                                  <span>Eff {fmtDate(q.proposed_effective)}</span>
                                ) : q.quote_date ? (
                                  <span>Quoted {fmtDate(q.quote_date)}</span>
                                ) : null}
                                {q.coverage_type ? <span>{q.coverage_type}</span> : null}
                                {q.underwriter ? <span>UW: {q.underwriter}</span> : null}
                              </div>
                            </div>
                            {(() => {
                              const chip = mirrorChip(q);
                              if (!chip) return null;
                              return (
                                <span
                                  role="button"
                                  tabIndex={0}
                                  title={chip.title}
                                  aria-label={chip.title}
                                  onClick={(e) => doResync(q, e)}
                                  onKeyDown={(e) => { if (e.key === 'Enter') doResync(q, e); }}
                                  className={`inline-flex shrink-0 items-center gap-1 rounded-pill px-1.5 py-0.5 text-[9px] font-semibold cursor-pointer ${chip.className}`}
                                >
                                  {resyncingId === q.id
                                    ? <RefreshCw size={10} strokeWidth={2} className="animate-spin" />
                                    : <CloudOff size={10} strokeWidth={2} />}
                                  {chip.label}
                                </span>
                              );
                            })()}
                            {(() => {
                              const s = q.approval_summary || {};
                              const parts = [];
                              if (s.approved) parts.push({ k: 'a', label: `${s.approved}✓`, cls: 'text-success' });
                              if (s.conditional) parts.push({ k: 'c', label: `${s.conditional} cond`, cls: 'text-warning' });
                              if (s.declined) parts.push({ k: 'd', label: `${s.declined} decl`, cls: 'text-danger' });
                              if (!parts.length) return null;
                              return (
                                <span
                                  className="inline-flex shrink-0 items-center gap-1 rounded-pill border border-border bg-bg/60 px-1.5 py-0.5 text-[9px] font-semibold"
                                  title="Per-carrier pilot approval (approved / conditional / declined)"
                                >
                                  {parts.map((p) => <span key={p.k} className={p.cls}>{p.label}</span>)}
                                </span>
                              );
                            })()}
                            {q.status ? (
                              <Badge
                                variant={STATUS_VARIANT[q.status] || 'outline'}
                                className="shrink-0 text-[9px]"
                              >
                                {(STATUS_LABEL[q.status] || q.status).toUpperCase()}
                              </Badge>
                            ) : null}
                            {/* MV-03 §2.3: derived offer-expiry chip (server
                                annotates from quote_expires_at + admin lead). */}
                            {q.expiry?.expiring ? (
                              <Badge variant="warning" className="shrink-0 text-[9px]">
                                {q.expiry.expired
                                  ? 'OFFER EXPIRED'
                                  : q.expiry.days_left === 0
                                    ? 'OFFER EXPIRES TODAY'
                                    : `OFFER EXPIRES IN ${q.expiry.days_left}D`}
                              </Badge>
                            ) : null}
                            {['received', 'presented', 'selected'].includes(q.status) ? (
                              <span
                                role="button"
                                tabIndex={0}
                                aria-label={`Record offline bind for ${q.carrier || 'quote'}`}
                                title="Bound with carrier — record a bind agreed offline (phone / closing table). Holds the quoted effective date and sends the client straight to signing."
                                onClick={(e) => { e.stopPropagation(); setBindTarget(q); }}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setBindTarget(q); } }}
                                className="shrink-0 cursor-pointer p-1 text-text-dim transition hover:text-success"
                              >
                                <Handshake size={12} strokeWidth={1.5} />
                              </span>
                            ) : null}
                            {q.client_visible ? (
                              <span
                                role="button"
                                tabIndex={0}
                                aria-label={`Withdraw ${q.carrier || 'quote'} from Tower`}
                                title="Withdraw from Tower — the client stops seeing this quote (reversible)."
                                onClick={(e) => { e.stopPropagation(); setWithdrawTarget(q); }}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setWithdrawTarget(q); } }}
                                className="shrink-0 cursor-pointer p-1 text-text-dim transition hover:text-warning"
                              >
                                <EyeOff size={12} strokeWidth={1.5} />
                              </span>
                            ) : null}
                            <span
                              role="button"
                              tabIndex={0}
                              aria-label={`Delete quote from ${q.carrier || 'unknown carrier'}`}
                              onClick={(e) => { e.stopPropagation(); setDeleteTarget(q); }}
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setDeleteTarget(q); } }}
                              className="shrink-0 cursor-pointer p-1 text-text-dim transition hover:text-danger"
                            >
                              <Trash2 size={12} strokeWidth={1.5} />
                            </span>
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
              );
            })}
          </ul>
        )}
      </Panel>

      <QuoteEntrySheet
        dealId={sheetTarget?.dealId || null}
        quotationId={sheetTarget?.quotationId || null}
        seedQuote={sheetTarget?.seed || null}
        open={!!sheetTarget}
        onOpenChange={(o) => !o && setSheetTarget(null)}
        onSaved={handleSaved}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && !deleting && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this quote?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget ? `${deleteTarget.carrier || '(no carrier)'} — ${fmtMoney(deleteTarget.premium)}. ` : ''}
              Permanently deletes this quote (and its CRM mirror). This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} disabled={deleting}>
              {deleting ? 'Deleting…' : 'Delete quote'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!bindTarget} onOpenChange={(o) => !o && !bindingOffline && setBindTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Bound with carrier?</AlertDialogTitle>
            <AlertDialogDescription>
              {bindTarget ? `${bindTarget.carrier || '(no carrier)'} — ${fmtMoney(bindTarget.premium)}${bindTarget.proposed_effective ? `, effective ${fmtDate(bindTarget.proposed_effective)}` : ''}. ` : ''}
              Records the bind exactly as quoted. The effective date holds even if it is in the past
              (coverage is already bound with the carrier). The quote becomes visible to the client and
              their Tower link goes straight to the signing steps, with the date locked.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bindingOffline}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doRecordBind} disabled={bindingOffline}>
              {bindingOffline ? 'Recording…' : 'Record bind'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!withdrawTarget} onOpenChange={(o) => !o && !withdrawing && setWithdrawTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Withdraw this quote from the Tower?</AlertDialogTitle>
            <AlertDialogDescription>
              {withdrawTarget ? `${withdrawTarget.carrier || '(no carrier)'} — ${fmtMoney(withdrawTarget.premium)}. ` : ''}
              The client stops seeing this quote within seconds. The quote isn't deleted — you can present it again later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={withdrawing}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doWithdraw} disabled={withdrawing}>
              {withdrawing ? 'Withdrawing…' : 'Withdraw from Tower'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
